import type { ReactElement } from 'react'

interface IconProps {
  name: IconName
  size?: number
  className?: string
  strokeWidth?: number
}

export type IconName =
  | 'arrow-left'
  | 'arrow-right'
  | 'camera'
  | 'image'
  | 'edit'
  | 'check'
  | 'flash'
  | 'flash-off'
  | 'document'
  | 'building'
  | 'info'
  | 'lock'
  | 'switch-camera'
  | 'logout'
  | 'alert-triangle'
  | 'x'

const icons: Record<IconName, (props: { strokeWidth: number }) => ReactElement> = {
  'arrow-left': ({ strokeWidth }) => (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M15 19l-7-7 7-7" />
  ),
  'arrow-right': ({ strokeWidth }) => (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M9 5l7 7-7 7" />
  ),
  'camera': ({ strokeWidth }) => (
    <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
    </>
  ),
  'image': ({ strokeWidth }) => (
    <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </>
  ),
  'edit': ({ strokeWidth }) => (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  ),
  'check': ({ strokeWidth }) => (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M5 13l4 4L19 7" />
  ),
  'flash': ({ strokeWidth }) => (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M13 10V3L4 14h7v7l9-11h-7z" />
  ),
  'flash-off': ({ strokeWidth }) => (
    <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M13 10V3L4 14h7v7l9-11h-7z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M3 3l18 18" />
    </>
  ),
  'document': ({ strokeWidth }) => (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  ),
  'building': ({ strokeWidth }) => (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  ),
  'info': ({ strokeWidth }) => (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  ),
  'lock': ({ strokeWidth }) => (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  ),
  'switch-camera': ({ strokeWidth }) => (
    <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M16 13l-2-2m0 0l-2 2m2-2v6" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M8 13l2 2m0 0l2-2m-2 2V9" />
    </>
  ),
  'logout': ({ strokeWidth }) => (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  ),
  'alert-triangle': ({ strokeWidth }) => (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  ),
  'x': ({ strokeWidth }) => (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d="M6 18L18 6M6 6l12 12" />
  ),
}

export function Icon({ name, size = 24, className = '', strokeWidth = 2 }: IconProps) {
  const IconPath = icons[name]

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      className={className}
    >
      <IconPath strokeWidth={strokeWidth} />
    </svg>
  )
}
